import { useId } from "react";

import { InfiniteScrollWithQuery } from "shared/ui/infinite-scroll-with-observer/react-query";
import { DataStatesWrapper } from "shared/ui/data-states-wrapper";
import { ChatMessages } from "shared/ui/messages/components/view/messages";
import { ChatInput } from "shared/ui/messages/components/view/input";
import { ChatHeader } from "shared/ui/messages/components/view/header";
import { ChatViewStates } from "shared/ui/messages/components/view/states";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";
import { INFINITE_SCROLL_DIRECTIONS } from "shared/ui/infinite-scroll-with-observer/constants";
import { clsxMerge } from "shared/lib/helpers";
import { TIME_INTERVALS } from "shared/lib/constants/time";
import { ChatContactI } from "shared/ui/messages/types";

import { useMessagingContext } from "../context";

export const ChatView = ({ className }: PropsWithClassNameI) => {
  const {
    selectedChat,
    selectedChatId,
    selectedContact,
    messagesQuery,
    sendMessageMutation,
    currentUser,
    onlineStatusesQuery,
    selectedContactRole,
    hasContactToChat,
  } = useMessagingContext();
  const onlineStatusMap = onlineStatusesQuery.data;

  const { data: messages, isFetchingNextPage } = messagesQuery;

  const isMessagesHistoryEmpty = Boolean(
    !messagesQuery.isFetching && (!messages || messages.length === 0)
  );

  const chatMessagesInfiniteScrollIndicatorId = useId();

  if (!selectedChatId) {
    return <ChatViewStates.Zero />;
  }

  // Comparing with false explicitly, as undefined is also a possible value
  if (hasContactToChat === false) {
    return <ChatViewStates.Error />;
  }

  return (
    <div className={clsxMerge("flex h-full flex-col", className)}>
      <ChatHeader
        onlineStatusMap={onlineStatusMap}
        role={selectedContactRole}
        contact={selectedContact}
      />

      <InfiniteScrollWithQuery
        isDefaultMessageHidden
        api={{
          ...messagesQuery,
          isEmpty: isMessagesHistoryEmpty,
        }}
        scrollDirection={INFINITE_SCROLL_DIRECTIONS.UP}
        withCustomScrollIndicator
        scrollIndicatorId={chatMessagesInfiniteScrollIndicatorId}
        loadMoreThrottleInterval={TIME_INTERVALS.ONE_SECOND}
      >
        <DataStatesWrapper
          api={{
            ...messagesQuery,
            isFetching: messagesQuery.isPending,
            isEmpty: isMessagesHistoryEmpty || selectedChat?.isDraft,
          }}
          loading={<ChatViewStates.Loading />}
          empty={<ChatViewStates.Empty />}
          error={<ChatViewStates.Error />}
        >
          <ChatMessages
            key={selectedContact?.id}
            messages={messages}
            me={currentUser}
            counterparty={selectedContact as ChatContactI}
            onlineUserMap={onlineStatusMap}
            isFetchingNextPage={isFetchingNextPage}
            scrollIndicatorId={chatMessagesInfiniteScrollIndicatorId}
          />
        </DataStatesWrapper>
      </InfiniteScrollWithQuery>

      <ChatInput
        bottomNote="This message thread will contain all your messages across all accounts with this AE."
        onSendMessage={sendMessageMutation.mutate}
      />
    </div>
  );
};
